import * as fabric from "@/models/products/fabric";

import axios, { AxiosResponse } from "axios";
import { APIError, NotFoundError } from "./error_service";

/**
 * Creates a new Fabric Service

 * @example
 * const fabricService = new FabricService();
 * fabricService.getFabrics();
 */

import { AppHeaders } from "@/models/api/api";

axios.defaults.headers = { apikey: process.env.VUE_APP_API_KEY } as AppHeaders;
axios.defaults.baseURL = process.env.VUE_APP_API_NODE_URL;

export default class FabricService {
  /**
   * Fetch fabrics with optional filtering. Mirrors shade-designer's API
   * surface so per-component pickers (binding, fringe, main) can request
   * the right catalog directly from the backend.
   *
   * Polycotton fabrics are filtered out client-side unless the request is
   * for fringe or binding fabrics — they are not valid main-canopy options.
   *
   * @returns response array of fabric swatches
   */
  public async getFabrics(optionsObject?: {
    grade?: fabric.FabricGrade[];
    mfr?: fabric.FabricMfr;
    isFringe?: boolean;
    isBinding?: boolean;
    isAdmin?: boolean;
  }): Promise<fabric.FabricSwatch[]> {
    const params = new URLSearchParams();
    if (optionsObject) {
      if (optionsObject.grade && optionsObject.grade.length > 0) {
        for (const g of optionsObject.grade) {
          params.append("grade", g);
        }
      }
      if (optionsObject.mfr) {
        params.append("mfr", String(optionsObject.mfr));
      }
      if (optionsObject.isFringe) {
        params.append("fringe", "true");
      }
      if (optionsObject.isBinding) {
        params.append("binding", "true");
      }
      if (optionsObject.isAdmin) {
        params.append("admin", "true");
      }
    }
    const qs = params.toString();
    const url = "/fabrics" + (qs ? "?" + qs : "");
    let fabrics: fabric.FabricSwatch[];
    try {
      const res: AxiosResponse = await axios.get(url);
      fabrics = res.data.data;
    } catch (err) {
      throw new APIError(
        "Unable to get fabric data. Please try again.",
        err.response.status
      );
    }
    // Polycotton is only valid for fringe/binding components.
    const allowPolycotton = !!(
      optionsObject &&
      (optionsObject.isFringe || optionsObject.isBinding)
    );
    if (!allowPolycotton) {
      fabrics = fabrics.filter(f => f.fabric_type !== "polycotton");
    }
    return fabrics;
  }

  /**
   * Fetch all fabrics (no grade filter) for global search pools. Accepts
   * the same fringe/binding flags as getFabrics so callers can scope the
   * search to a fabric category when needed.
   */
  public async getAllFabrics(optionsObject?: {
    isFringe?: boolean;
    isBinding?: boolean;
  }): Promise<fabric.FabricSwatch[]> {
    return this.getFabrics({
      isFringe: optionsObject?.isFringe,
      isBinding: optionsObject?.isBinding
    });
  }

  /**
   * Fetch a single fabric by mfr_code (or alt_mfr_code). The API may return
   * multiple matches (duplicate mfr_codes across brand collections); this
   * method returns only the first match. Callers that need to disambiguate
   * should use `getSwatchesByCode()` below and pick from the array.
   *
   * @param {number | string} mfr_code - matched server-side against both
   *        mfr_code and alt_mfr_code.
   * @returns {FabricSwatch} - first fabric returned for the given code
   */
  public async getSingleFabric(
    mfr_code: number | string
  ): Promise<fabric.FabricSwatch> {
    const fabrics = await this.getSwatchesByCode(mfr_code);
    return fabrics[0];
  }

  /**
   * Fetch all fabrics matching a mfr_code (or alt_mfr_code). The API returns
   * an array because a single mfr_code can now match multiple fabrics across
   * brand collections (e.g. SENS "D-020" exists in Coquette, Finesse, Enea).
   *
   * @param {number | string} mfr_code
   * @param {object} filters - optional server-side disambiguation filters
   * @returns {FabricSwatch[]}
   */
  public async getSwatchesByCode(
    mfr_code: number | string,
    filters?: { group?: string; brand_collection?: string; grade?: string }
  ): Promise<fabric.FabricSwatch[]> {
    let url = "/fabrics/" + mfr_code;
    const params: string[] = [];
    // `group` is the new disambiguator (filters by name.group server-side).
    if (filters?.group) params.push(`group=${encodeURIComponent(filters.group)}`);
    // `brand_collection` retained for backwards compat with any caller still
    // using the legacy disambiguator.
    if (filters?.brand_collection) params.push(`brand_collection=${encodeURIComponent(filters.brand_collection)}`);
    if (filters?.grade) params.push(`grade=${encodeURIComponent(filters.grade)}`);
    if (params.length) url += "?" + params.join("&");
    try {
      const res: AxiosResponse = await axios.get(url);
      const data = res.data.data;
      return Array.isArray(data) ? data : [data];
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Fabric swatch not found.",
          err.response.status
        );
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
  }
}
